import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Popconfirm,
  message,
  Tag,
} from "antd";
import * as Icons from "@ant-design/icons";
import {
  getPermissions,
  updatePermission,
  createPermission,
  deletePermission,
} from "../../api/setting/Permission";

const sessiData = JSON.parse(sessionStorage.getItem("user"));
const { Option } = Select;
const iconList = Object.keys(Icons).filter((name) => name.endsWith("Outlined"));
const getIconComponent = (name) => {
  const IconComponent = Icons[name];
  return IconComponent ? <IconComponent /> : null;
};

const PermissionPage = () => {
  const [data, setData] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (editingPermission) {
      const status_ = editingPermission.pms_status == "Aktif" ? 1 : 0;
      form.setFieldsValue({
        nama: editingPermission.pms_nama,
        type: editingPermission.pms_type,
        deskripsi: editingPermission.pms_deskripsi,
        url: editingPermission.pms_url,
        icon: editingPermission.pms_icon,
        ordinal: editingPermission.pms_ordinal,
        parent: editingPermission.pms_parent,
        status: status_,
      });
    }
  }, [editingPermission, form]);

  const fetchPermissions = async () => {
    setIsLoadingTable(true);
    try {
      const permissionsData = await getPermissions();
      setIsLoadingTable(false);
      setData(permissionsData.data);
    } catch (error) {
      setIsLoadingTable(false);
      message.error(`Failed to fetch roles: ` + error);
    }
  };

  const showModal = (record = null) => {
    setIsModalVisible(true);
    if (record) {
      setEditingPermission(record);
    } else {
      setEditingPermission(null);
      form.resetFields();
    }
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const cleanedValues = Object.fromEntries(
          Object.entries(values).map(([key, val]) => [
            key,
            val === "" ? null : val,
          ])
        );

        const payload = {
          ...values,
          parent: values.parent ?? null,
          status: values.status ? "Aktif" : "Tidak Aktif",
          createdBy: sessiData?.[0]?.user?.[0]?.fullname || "Unknown",
        };

        const action = editingPermission
          ? updatePermission(editingPermission.pms_id, payload)
          : createPermission(payload);

        action
          .then((response) => {
            response.status
              ? message.success(response.message)
              : message.error(response.message);

            setIsModalVisible(false);
            form.resetFields();
            fetchPermissions();
          })
          .catch((err) => {
            console.error("API Error:", err);
            message.error(
              editingPermission
                ? "Gagal memperbarui permission"
                : "Gagal menambahkan permission"
            );
          });
      })
      .catch((err) => {
        message.error("Validasi gagal:" + err);
      });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleDelete = async (key) => {
    const hasChild = data.some((item) => item.pms_parent === key);
    if (hasChild) {
      message.warning(
        "Permission tidak bisa dihapus karena masih memiliki child permission"
      );
      return;
    }
    try {
      const payload = {
        role_id: key,
      };
      const response = await deletePermission(payload);
      fetchPermissions();
      response.status
        ? message.success(response.message)
        : message.error(response.message);
    } catch (error) {
      const errorMessage =
        error?.message || "Terjadi kesalahan saat menghapus data";
      message.error(errorMessage);
    }
  };

  const handleFormChange = (changedValues, allValues) => {
    if (changedValues.type) {
      setSelectedType(changedValues.type);
      if (changedValues.type === "Button") {
        form.setFieldsValue({ parent: null });
      }
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<Icons.SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Cari
        </Button>
        <Button
          onClick={() => {
            handleReset(clearFilters);
            confirm({ closeDropdown: true });
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <Icons.SearchOutlined
        style={{ color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: "#ffc069", padding: 0 }}>{text}</span>
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
  };

  const columns = [
    {
      title: "Nama",
      dataIndex: "pms_nama",
      key: "pms_nama",
      sorter: (a, b) => a.pms_nama.localeCompare(b.pms_nama),
    },
    {
      title: "Type",
      dataIndex: "pms_type",
      key: "pms_type",
      filters: [
        { text: "Page", value: "Page" },
        { text: "Button", value: "Button" },
      ],
      onFilter: (value, record) => record.pms_type === value,
    },
    {
      title: "Deskripsi",
      dataIndex: "pms_deskripsi",
      key: "pms_deskripsi",
      ...getColumnSearchProps("pms_deskripsi"),
    },
    {
      title: "Icon",
      dataIndex: "pms_icon",
      key: "pms_icon",
      render: (icon) => {
        const IconComponent = Icons[icon];
        return IconComponent ? <IconComponent /> : "-";
      },
    },
    {
      title: "URL",
      dataIndex: "pms_url",
      key: "pms_url",
    },
    {
      title: "Parent",
      dataIndex: "parent_name",
      key: "parent_name",
      filters: Array.from(new Set(data.map((item) => item.parent_name)))
        .filter(Boolean)
        .map((parent) => ({ text: parent, value: parent })),
      onFilter: (value, record) => record.parent_name === value,
    },
    {
      title: "Urutan",
      dataIndex: "pms_ordinal",
      key: "pms_ordinal",
      sorter: (a, b) => a.pms_ordinal - b.pms_ordinal,
    },
    {
      title: "Status",
      dataIndex: "pms_status",
      key: "pms_status",
      render: (pms_status) =>
        pms_status === "Aktif" ? (
          <Tag color="green">Aktif</Tag>
        ) : (
          <Tag color="red">Tidak Aktif</Tag>
        ),
      filters: [
        { text: "Aktif", value: "Aktif" },
        { text: "Tidak Aktif", value: "Tidak Aktif" },
      ],
      onFilter: (value, record) => record.pms_status === value,
    },
    {
      title: "Aksi",
      key: "action",
      render: (_, record) => (
        <span style={{ display: "flex", gap: 8 }}>
          <Button type="link" onClick={() => showModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title={`Yakin ingin menghapus "${record.pms_nama}"?`}
            onConfirm={() => handleDelete(record.pms_id)}
            okText="Ya"
            cancelText="Batal"
          >
            <Button type="link" danger>
              Hapus
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        onClick={() => showModal()}
        style={{ marginBottom: 16 }}
      >
        + Tambah Permission
      </Button>
      <Table
        columns={columns}
        dataSource={data}
        loading={isLoadingTable}
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
      />

      <Modal
        title={editingPermission ? "Edit Permission" : "Tambah Permission"}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
          <Form.Item
            name="nama"
            label="Nama"
            rules={[{ required: true, message: "Nama wajib diisi" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select>
              <Option value="Page">Page</Option>
              <Option value="Button">Button</Option>
            </Select>
          </Form.Item>
          <Form.Item name="deskripsi" label="Deskripsi">
            <Input />
          </Form.Item>
          <Form.Item name="icon" label="Icon">
            <Select
              showSearch
              placeholder="Pilih Icon"
              optionLabelProp="label"
              filterOption={(input, option) =>
                option?.value?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {iconList.map((name) => (
                <Select.Option key={name} value={name} label={name}>
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {getIconComponent(name)}
                    {name}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="url" label="URL">
            <Input />
          </Form.Item>
          <Form.Item name="parent" label="Parent">
            <Select
              allowClear
              placeholder="Pilih Parent (opsional)"
              showSearch
              optionFilterProp="children"
              disabled={selectedType === "Button"}
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {data
                .filter(
                  (item) =>
                    item.pms_url === null &&
                    item.pms_type === "Page" &&
                    item.pms_status === "Aktif"
                )
                .map((item) => (
                  <Select.Option
                    key={item.pms_id}
                    value={item.pms_id}
                    disabled={
                      editingPermission &&
                      editingPermission.pms_id === item.pms_id
                    }
                  >
                    {item.pms_deskripsi}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item name="ordinal" label="Urutan">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="status" label="Status" valuePropName="checked">
            <Switch checkedChildren="Aktif" unCheckedChildren="Tidak Aktif" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionPage;
